import { Box, Button, Divider, Tooltip, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined'
import { BigNumber } from 'ethers'
import { useNetwork } from 'wagmi'
import { show } from '@ebay/nice-modal-react'
import StakeDialog from '../StakeDialog'
import ClaimDialog from '../ClaimDialog'
import UnstakeDialog from '../UnstakeDialog'
import Countdown from './Countdown'
import Earned from './Earned'
import YourStaked from './YourStaked'
import ExternalLinkGroup from './ExternalLinkGroup'
import TotalStaked from './TotalStaked'
import { FlexCol } from '@/components/common/Columns'
import { RowBetweenCenter, RowBetweenX, RowCenterY } from '@/components/common/Rows'
import TokenLogo from '@/components/common/TokenLogo'
import { getTickerByErcAddress } from '@/utils'
import { getActivityStatus } from '@/hooks/useActivityState'
import { useUserEarned, useUserParticipation } from '@/hooks/farmHooks'
import Helper from '@/components/common/Helper'
import { useTokenPrice } from '@/hooks/useTokenPrice'

interface PoolCardProps {
  stakeTokenAddress: string
  rewardTokenAddress: string
  startAt: number
  endAt: number
  totalStakedAmount: BigNumber
  poolId: number
  onChange: () => void
  rewardPerSecond: BigNumber
  lockTime: number
  pollAtInterval: boolean
  loading: boolean
}

const PoolCard = ({
  stakeTokenAddress,
  rewardTokenAddress,
  startAt,
  endAt,
  totalStakedAmount,
  poolId,
  onChange,
  rewardPerSecond,
  lockTime,
  pollAtInterval,
  loading,
}: PoolCardProps) => {
  const { chain } = useNetwork()

  const stakeTicker = useMemo(() => getTickerByErcAddress(stakeTokenAddress), [stakeTokenAddress])
  const stakeToken = stakeTicker.erc20Tokens[chain?.id]
  const rewardTicker = useMemo(() => getTickerByErcAddress(rewardTokenAddress), [rewardTokenAddress])
  const rewardToken = rewardTicker.erc20Tokens[chain?.id]

  const [poolState, setPoolState] = useState(getActivityStatus(startAt, endAt))

  const { data: stakeTokenPrice } = useTokenPrice(stakeTicker.name)
  const { data: rewardTokenPrice } = useTokenPrice(rewardTicker.name)
  const isPriceBothReady = typeof stakeTokenPrice === 'number' && typeof rewardTokenPrice === 'number'

  const {
    data: userParticipation,
    refresh: refreshUserParticipation,
    loading: isLoadingUserParticipation,
  } = useUserParticipation(poolId)
  const {
    data: userEarned,
    refresh: refreshUserEarned,
    loading: isUserEarnedLoading,
  } = useUserEarned(poolId, { pollingInterval: pollAtInterval ? 3000 : undefined })

  const apyText =
    isPriceBothReady && poolState === 'on_going'
      ? totalStakedAmount.gt(0)
        ? rewardPerSecond
            .mul(86400 * 365)
            .mul((rewardTokenPrice * 1e10).toFixed())
            .div(totalStakedAmount.mul((stakeTokenPrice * 1e8).toFixed()))
            .toString()
        : '0'
      : '-'

  const getPoolShare = () => {
    if (!userParticipation?.stakedAmount || poolState !== 'on_going') {
      return '-'
    }
    if (totalStakedAmount.lte(0) || userParticipation.stakedAmount.lte(0)) {
      return '0'
    }
    if (
      BigNumber.from(userParticipation.stakedAmount)
        .mul(100 * 1000)
        .lt(totalStakedAmount)
    ) {
      return '< 0.001'
    }
    return (
      BigNumber.from(userParticipation.stakedAmount)
        .mul(100 * 1000)
        .div(totalStakedAmount)
        .toNumber() / 1000
    )
  }

  return (
    <Box sx={{ border: '1px solid rgba(191, 255, 11, 0.20)' }}>
      <RowBetweenCenter sx={{ bgcolor: '#1C1F0C', borderBottom: '1px solid rgba(191, 255, 11, 0.20)', px: 20, py: 12 }}>
        <FlexCol sx={{ rowGap: 12 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>Earn {rewardTicker.name}</Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>Stake {stakeTicker.name}</Typography>
        </FlexCol>

        <Box sx={{ position: 'relative', mr: 12 }}>
          <TokenLogo src={rewardTicker?.logoSrcSet.brc} size={56} alt="" />
          <TokenLogo
            src={stakeTicker?.logoSrcSet.brc}
            size={28}
            alt=""
            style={{ position: 'absolute', bottom: '-4px', right: '-12px' }}
          />
        </Box>
      </RowBetweenCenter>

      <FlexCol sx={{ px: 20, py: 16, gap: 16 }}>
        {isPriceBothReady ? (
          <RowBetweenX>
            <RowCenterY sx={{ columnGap: 4 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>APR</Typography>
              <Helper
                toolTipProps={{
                  placement: 'top',
                  title:
                    'Calculated based on current rates and subject to change based on various external variables. It is a rough estimate provided for convenience only, and by no means represents guaranteed returns.',
                  arrow: true,
                }}
                iconProps={{ sx: { width: 16, height: 16, color: '#FFFFFF80' } }}
              />
            </RowCenterY>
            <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>{apyText}%</Typography>
          </RowBetweenX>
        ) : (
          <RowBetweenX>
            <RowCenterY sx={{ columnGap: 4 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>Your Pool Share</Typography>
              <Helper
                toolTipProps={{
                  placement: 'top',
                  title:
                    'Calculated based on current rates and subject to change based on various external variables. It is a rough estimate provided for convenience only, and by no means represents guaranteed returns.',
                  arrow: true,
                }}
                iconProps={{ sx: { width: 16, height: 16, color: '#FFFFFF80' } }}
              />
            </RowCenterY>
            <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>{getPoolShare()}%</Typography>
          </RowBetweenX>
        )}

        <FlexCol sx={{ rowGap: 12 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>Your Staked</Typography>

          <RowBetweenCenter>
            <RowCenterY sx={{ columnGap: 8 }}>
              <TokenLogo src={stakeTicker.logoSrcSet.brc} size={24} alt="" />

              <YourStaked
                loading={isLoadingUserParticipation}
                tokenName={stakeTicker?.name}
                price={stakeTokenPrice}
                stakedAmount={userParticipation?.stakedAmount}
                decimal={stakeToken.decimal}
              />
            </RowCenterY>

            <RowCenterY sx={{ columnGap: 8 }}>
              <Tooltip arrow placement="top" title="Stake">
                <Button
                  disabled={poolState !== 'on_going'}
                  variant="outlined"
                  sx={{ width: 18, minWidth: 'unset', height: 18, p: 0 }}
                  onClick={() => {
                    show(StakeDialog, {
                      tickerName: stakeTicker.name,
                      poolId,
                      lockTime,
                      onSuccess: () => {
                        onChange()
                        refreshUserParticipation()
                      },
                    })
                  }}
                >
                  <AddOutlinedIcon sx={{ width: 16, height: 16 }} />
                </Button>
              </Tooltip>

              <Tooltip arrow placement="top" title="Unstake">
                <Button
                  variant="outlined"
                  disabled={poolState === 'upcoming' || !userParticipation?.stakedAmount?.gt(0) || !userParticipation}
                  sx={{ width: 18, minWidth: 'unset', height: 18, p: 0 }}
                  onClick={() => {
                    if (!userParticipation) {
                      return
                    }
                    show(UnstakeDialog, {
                      tickerName: stakeTicker.name,
                      poolId,
                      stakedAmount: userParticipation?.stakedAmount,
                      endAt: userParticipation.unlockTimestamp.toNumber(),
                      lockTime,
                      onSuccess: () => {
                        onChange()
                        refreshUserParticipation()
                      },
                    })
                  }}
                >
                  <RemoveOutlinedIcon sx={{ width: 16, height: 16 }} />
                </Button>
              </Tooltip>
            </RowCenterY>
          </RowBetweenCenter>
        </FlexCol>

        <FlexCol sx={{ rowGap: 12 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>{rewardTicker?.name} Earned</Typography>

          <RowBetweenCenter>
            <Earned
              loading={isUserEarnedLoading}
              tokenName={rewardTicker.name}
              userEarned={userEarned}
              decimal={rewardToken.decimal}
              price={rewardTokenPrice}
            />

            <Button
              disabled={poolState === 'upcoming' || !userEarned?.gt(0) || !userParticipation?.unlockTimestamp}
              variant="contained"
              sx={{ px: 16, py: 8, ml: 'auto', fontSize: '14px', fontWeight: 700 }}
              onClick={() => {
                if (!userParticipation?.unlockTimestamp) {
                  return
                }
                show(ClaimDialog, {
                  tickerName: stakeTicker.name,
                  rewardTickerName: rewardTicker.name,
                  poolId,
                  price: rewardTokenPrice,
                  endAt: userParticipation?.unlockTimestamp.toNumber(),
                  lockTime,
                  onSuccess: () => {
                    onChange()
                    refreshUserEarned()
                    refreshUserParticipation()
                  },
                })
              }}
            >
              Claim
            </Button>
          </RowBetweenCenter>
        </FlexCol>
      </FlexCol>

      <Box sx={{ px: 20 }}>
        <Divider sx={{ borderColor: '#344205' }} />
      </Box>

      <FlexCol sx={{ px: 20, py: 16, gap: 16 }}>
        <TotalStaked
          loading={loading}
          decimal={stakeToken.decimal}
          tokenName={stakeTicker?.name}
          price={stakeTokenPrice}
          totalStakedAmount={totalStakedAmount}
        />

        <Countdown startAt={startAt} endAt={endAt} onStateChange={setPoolState} />

        <ExternalLinkGroup poolId={poolId} />
      </FlexCol>
    </Box>
  )
}

export default PoolCard
